/**
 * Kautionsabrechnungen — iter139 Welle 3
 *
 * Zwei Tabs:
 * 1) Abrechnungen (Liste + Details + Approval-Workflow)
 * 2) Zinstabelle (§ 551 BGB) — pflegen pro Jahr
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Plus, Copy, CheckCircle, XCircle, PaperPlaneTilt, ArrowClockwise, ShieldCheck, FileText, Trash, User, Buildings, CalendarBlank, Info, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import SearchableCombobox from "@/components/SearchableCombobox";
import MessagingDialog from "@/components/MessagingDialog";

const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };

const STATUS_LABEL = {
  draft: "Entwurf",
  pending_owner_approval: "Wartet auf Eigentümer-Freigabe",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
  sent: "Versandt",
  closed: "Abgeschlossen",
};
const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700",
  pending_owner_approval: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  sent: "bg-blue-100 text-blue-800",
  closed: "bg-slate-200 text-slate-800",
};

export default function Kaution() {
  const [tab, setTab] = useState("settlements");
  // 2026-03 — URL-Param `tenancy_id` → NewSettlementDialog vorbelegen und
  // direkt öffnen (aufgerufen aus DepositAccountPanel-Link).
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTenancyId = searchParams.get("tenancy_id");
  const [openNewFromUrl, setOpenNewFromUrl] = useState(false);
  useEffect(() => {
    if (initialTenancyId) {
      setOpenNewFromUrl(true);
      // Param sofort wieder aus URL entfernen, damit ein Reload nicht erneut
      // öffnet und beim erneuten Klick der Effekt sauber getriggert wird.
      const sp = new URLSearchParams(searchParams);
      sp.delete("tenancy_id");
      setSearchParams(sp, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [initialTenancyId]);

  return (
    <div className="space-y-4" data-testid="kaution-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck size={22} /> Kautionsabrechnungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">§ 551 BGB · tagesgenaue Verzinsung · Eigentümer-Freigabe vor Versand</p>
        </div>
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto">
        {[["settlements", "Abrechnungen"], ["rates", "Zinstabelle"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`kaution-tab-${k}`}
                  className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                    tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}>
            {v}
          </button>
        ))}
      </div>

      {tab === "settlements" && (
        <SettlementsTab
          openNewFromUrl={openNewFromUrl}
          onNewFromUrlHandled={() => setOpenNewFromUrl(false)}
          initialTenancyId={initialTenancyId}
        />
      )}
      {tab === "rates" && <RatesTab />}
    </div>
  );
}


/* ---------------------------------------------------------------- Settlements */
function SettlementsTab({ openNewFromUrl, onNewFromUrlHandled, initialTenancyId }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  // 2026-03 — Suchfilter (Mieter, Firmenname, Mietvertragsnummer, Objektname)
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kaution/settlements");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (openNewFromUrl) {
      setNewOpen(true);
      onNewFromUrlHandled?.();
    }
    /* eslint-disable-next-line */
  }, [openNewFromUrl]);

  // Suche über Kontakt-Namen, Vertragsnummer und Objekt-Label
  const filteredItems = (() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const parts = [
        s.contract_number || "",
        s.tenant_display_name || "",
        s.tenant_last_name || "",
        s.tenant_first_name || "",
        s.tenant_company_name || "",
        s.property_label || "",
        s.property_address_line || "",
      ].join(" ").toLowerCase();
      // Umlaut-tolerantes Match: einfache ä/ö/ü/ss-Ersetzung
      const canon = (v) => v.replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u")
                            .replace(/ß/g, "ss").replace(/ae/g, "a").replace(/oe/g, "o")
                            .replace(/ue/g, "u");
      return parts.includes(q) || canon(parts).includes(canon(q));
    });
  })();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">{filteredItems.length} von {items.length} Abrechnung(en)</div>
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="kaution-new-btn">
                <Plus size={14} /> Neue Abrechnung
              </Button>
            </DialogTrigger>
            <NewSettlementDialog
              onDone={() => { setNewOpen(false); load(); }}
              initialTenancyId={initialTenancyId}
            />
          </Dialog>
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Mieter, Firma, Vertragsnr., Objekt…"
          className="h-8 text-sm"
          data-testid="kaution-search"
        />
        <div className="bg-white border border-slate-200 rounded-sm divide-y" data-testid="kaution-list">
          {loading && <div className="p-4 text-slate-400 text-sm">Lädt…</div>}
          {!loading && filteredItems.length === 0 && (
            <div className="p-4 text-slate-400 text-sm">
              {search ? "Keine Treffer für deine Suche." : "Keine Abrechnungen — noch anlegen oder MV beenden."}
            </div>
          )}
          {filteredItems.map((s) => (
            <button key={s.id}
                    onClick={() => { api.get(`/kaution/settlements/${s.id}`).then(({ data }) => setSelected(data)); }}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selected?.id === s.id ? "bg-blue-50" : ""}`}
                    data-testid={`kaution-item-${s.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">
                  {s.tenant_display_name || s.contract_number || `MV bis ${fmtDate(s.end_of_tenancy_date)}`}
                </div>
                <Badge className={STATUS_COLOR[s.status]}>{STATUS_LABEL[s.status] || s.status}</Badge>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                {s.contract_number ? `${s.contract_number} · ` : ""}
                MV-Ende {fmtDate(s.end_of_tenancy_date)}
                {s.property_label ? ` · ${s.property_label}` : ""}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
                Netto: {fmtEUR(s.net_return)} · Zinsen: {fmtEUR(s.interest_total)}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-3">
        {selected ? (
          <SettlementDetail settlement={selected} onChange={(s) => { setSelected(s); load(); }} onDelete={() => { setSelected(null); load(); }} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm p-8 text-center text-slate-400">
            Bitte eine Abrechnung auswählen.
          </div>
        )}
      </div>
    </div>
  );
}

// 2026-02 — Mobile-freundlicher PDF-Öffner. Safari/iOS blockt window.open aus
// asynchronen Promises; deshalb legen wir einen unsichtbaren <a>-Tag an und
// klicken diesen. `target=_blank` funktioniert dann auch dort zuverlässig.
async function openPdfInline(url) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blob = res.data;
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    // Auf Mobile fällt der neue-Tab-Trick öfter durch — zusätzlich Download
    // erlauben, damit iOS Safari das PDF in der Vorschau öffnet.
    a.download = "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 120000);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "PDF konnte nicht geladen werden");
  }
}

function SettlementDetail({ settlement, onChange, onDelete }) {
  const [magicToken, setMagicToken] = useState(settlement.magic_link_token || null);
  const [comment, setComment] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgCtx, setMsgCtx] = useState(null);
  const s = settlement;

  const requestApproval = async () => {
    try {
      const { data } = await api.post(`/kaution/settlements/${s.id}/request-approval`);
      setMagicToken(data.magic_link_token);
      toast.success("Freigabe-Anfrage erzeugt. Magic-Link kopieren und an Eigentümer senden.");
      const { data: fresh } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const approveDirect = async () => {
    try {
      await api.post(`/kaution/settlements/${s.id}/approve`);
      toast.success("Freigegeben");
      const { data } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  // 2026-02 — Manuelle Freigabe (z. B. telefonisch), umgeht Magic-Link/Portal.
  const approveManual = async () => {
    const channels = { p: "phone", e: "email", i: "in_person", o: "other" };
    const c = window.prompt(
      "Wie hat der Eigentümer freigegeben?\n\n  p = Telefonisch (Standard)\n  e = E-Mail\n  i = Persönlich\n  o = Sonstige\n\nBuchstabe eingeben:",
      "p",
    );
    if (c === null) return;
    const channel = channels[(c || "p").toLowerCase().trim()] || "phone";
    const note = window.prompt(
      'Optionale Notiz (z. B. "Herr Müller telefonisch, 09.09.2026"):',
      "",
    );
    try {
      await api.post(`/kaution/settlements/${s.id}/approve-manual`, {
        channel, note: note || null,
      });
      toast.success("Freigabe erfasst — Abrechnung ist nun freigegeben.");
      const { data } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  // 2026-02 — Direktversand an Mieter (E-Mail): Anschreiben-Text im Body,
  // Kautionsabrechnung als PDF-Anhang. Kein Composer, keine Blob-URL — läuft
  // auch auf Mobile.
  const sendToTenant = async () => {
    if (!await window._domivusConfirm(
      "Kautionsabrechnung jetzt per E-Mail an den Mieter versenden?\n\n" +
      "· Das Anschreiben wird als E-Mail-Text formatiert.\n" +
      "· Die Kautionsabrechnung wird als PDF-Anhang mitgeschickt."
    )) return;
    try {
      const { data } = await api.post(`/kaution/settlements/${s.id}/send-to-tenant-direct`, {});
      toast.success(`Versendet an ${data.to}`);
      const { data: fresh } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    }
  };

  const rejectDirect = async () => {
    const reason = window.prompt("Ablehnungsgrund:");
    if (!reason) return;
    try {
      await api.post(`/kaution/settlements/${s.id}/reject`, { reason });
      toast.success("Abgelehnt");
      const { data } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/kaution/settlements/${s.id}/comments`, { body: comment });
      setComment("");
      const { data } = await api.get(`/kaution/settlements/${s.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const del = async () => {
    if (!await window._domivusConfirm("Diese Abrechnung löschen?")) return;
    try {
      await api.delete(`/kaution/settlements/${s.id}`);
      toast.success("Gelöscht");
      onDelete();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const magicUrl = magicToken ? `${window.location.origin}/kaution-freigabe/${magicToken}` : null;

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4" data-testid="kaution-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Kautionsabrechnung</div>
          <div className="text-lg font-semibold">MV-Ende: {fmtDate(s.end_of_tenancy_date)}</div>
          <div className="text-xs text-slate-500">Berechnet am {fmtDate(s.calculation_date)}</div>
        </div>
        <Badge className={STATUS_COLOR[s.status]}>{STATUS_LABEL[s.status] || s.status}</Badge>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <StatCell label="Kapital (Raten)" value={fmtEUR(s.principal_total)} />
        <StatCell label="Zinsen § 551" value={fmtEUR(s.interest_total)} />
        <StatCell label="Abzüge" value={fmtEUR(s.deductions_total)} />
        <StatCell label="Netto Auszahlung" value={fmtEUR(s.net_return)} highlight />
      </div>

      {s.interest_lines?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Zinsberechnung (tagesgenau, cross-year)</div>
          <div className="border border-slate-200 rounded max-h-40 overflow-y-auto">
            <table className="min-w-full text-xs" data-testid="kaution-interest-lines">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1">Jahr</th>
                  <th className="px-2 py-1">Zeitraum</th>
                  <th className="px-2 py-1 text-right">Kapital</th>
                  <th className="px-2 py-1 text-right">Tage</th>
                  <th className="px-2 py-1 text-right">Zins %</th>
                  <th className="px-2 py-1 text-right">Zins</th>
                </tr>
              </thead>
              <tbody>
                {s.interest_lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1">{l.year}</td>
                    <td className="px-2 py-1 text-slate-500">{fmtDate(l.period_from)}—{fmtDate(l.period_to)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(l.principal)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{l.days}/{l.days_basis}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{Number(l.rate_pct).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtEUR(l.interest)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {s.deductions?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Abzüge</div>
          <ul className="space-y-1 text-sm">
            {s.deductions.map((d, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span>{d.description} <span className="text-slate-500">({d.category})</span></span>
                <span className="tabular-nums">{fmtEUR(d.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 2026-03 — PDF-Aktionen (Vorschau + Anschreiben) */}
      <div className="border-t border-slate-200 pt-3 flex flex-wrap gap-2">
        <button type="button"
          onClick={() => openPdfInline(`/kaution/settlements/${s.id}/settlement.pdf`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          data-testid="kaution-preview-pdf">
          <FileText size={14} /> Abrechnungs-PDF (Vorschau)
        </button>
        <button type="button"
          onClick={() => openPdfInline(`/kaution/settlements/${s.id}/cover-letter.pdf`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          data-testid="kaution-preview-cover">
          <PaperPlaneTilt size={14} /> Anschreiben Eigentümer (Freigabe)
        </button>
        <button type="button"
          onClick={() => openPdfInline(`/kaution/settlements/${s.id}/tenant-letter.pdf`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          data-testid="kaution-preview-tenant">
          <PaperPlaneTilt size={14} /> Anschreiben Mieter
        </button>
      </div>

      {/* Approval Workflow */}
      <div className="border-t border-slate-200 pt-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">Eigentümer-Freigabe</div>
        {(s.status === "draft" || s.status === "rejected" || s.status === "pending_owner_approval") && (
          <div className="flex flex-wrap gap-2">
            {(s.status === "draft" || s.status === "rejected") && (
              <Button size="sm" onClick={requestApproval} className="bg-amber-600 hover:bg-amber-700 gap-1" data-testid="kaution-request-approval">
                <PaperPlaneTilt size={14} /> Freigabe anfordern
              </Button>
            )}
            {s.status === "pending_owner_approval" && (
              <>
                <Button size="sm" onClick={approveDirect} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="kaution-approve-direct">
                  <CheckCircle size={14} /> Selbst freigeben
                </Button>
                <Button size="sm" variant="outline" onClick={rejectDirect} className="text-rose-700 border-rose-300 gap-1" data-testid="kaution-reject">
                  <XCircle size={14} /> Ablehnen
                </Button>
              </>
            )}
            {/* 2026-02 — Manuelle Freigabe (z. B. telefonisch) — für alle Status vor Freigabe verfügbar */}
            <Button size="sm" variant="outline" onClick={approveManual}
                    className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid="kaution-approve-manual">
              <Phone size={14} /> Freigabe telefonisch/persönlich erfassen
            </Button>
          </div>
        )}
        {magicUrl && s.status === "pending_owner_approval" && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1" data-testid="kaution-magic-link">
            <div className="text-xs font-semibold text-amber-900">Magic-Link (14 Tage gültig)</div>
            <div className="text-xs break-all font-mono bg-white p-1.5 rounded border border-amber-300">{magicUrl}</div>
            <Button size="sm" variant="outline" className="gap-1" data-testid="kaution-copy-magic"
              onClick={() => { navigator.clipboard.writeText(magicUrl); toast.success("Link kopiert"); }}>
              <Copy size={14} /> Link kopieren
            </Button>
          </div>
        )}
        {s.owner_approved_at && (
          <div className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={12} /> Freigegeben am {fmtDate(s.owner_approved_at)}
            {s.owner_approved_channel && s.owner_approved_channel !== "portal" && (
              <span className="text-slate-500"> · Kanal: {s.owner_approved_channel === "phone" ? "Telefon" : s.owner_approved_channel === "in_person" ? "Persönlich" : s.owner_approved_channel === "email" ? "E-Mail" : s.owner_approved_channel}</span>
            )}
          </div>
        )}
        {s.owner_rejected_at && (
          <div className="text-xs text-rose-700 flex items-center gap-1"><XCircle size={12} /> Abgelehnt am {fmtDate(s.owner_rejected_at)}: {s.owner_rejection_reason}</div>
        )}
      </div>

      {/* 2026-02 — Versand an den Mieter */}
      <div className="border-t border-slate-200 pt-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">Versand an Mieter</div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" onClick={sendToTenant}
                  className="bg-slate-900 hover:bg-slate-800 gap-1"
                  data-testid="kaution-send-tenant">
            <PaperPlaneTilt size={14} /> Abrechnung an Mieter versenden
          </Button>
          {s.tenant_letter_sent_at ? (
            <span className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle size={12} /> Versendet am {fmtDate(s.tenant_letter_sent_at)}
              {(s.tenant_letter_sent_channels || []).length > 0 && (
                <span className="text-slate-500"> · {s.tenant_letter_sent_channels.join(" / ")}</span>
              )}
            </span>
          ) : (
            <span className="text-xs text-slate-500">Noch kein Versand erfolgt</span>
          )}
        </div>
        {!s.owner_approved_at && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5">
            <Info size={12} className="shrink-0 mt-0.5" />
            <span>Hinweis: Der Eigentümer hat noch nicht freigegeben. Für einen sauberen Prüfpfad zuerst „Freigabe telefonisch/persönlich erfassen" nutzen.</span>
          </div>
        )}
      </div>

      {/* Kommentare */}
      <div className="border-t border-slate-200 pt-3 space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">Kommentare</div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {(s.comments || []).map((c) => (
            <div key={c.id} className={`text-xs p-2 rounded ${c.author_role === "owner" ? "bg-amber-50" : "bg-slate-50"}`}>
              <div className="font-semibold">{c.author_name} <span className="text-slate-400">({c.author_role})</span></div>
              <div>{c.body}</div>
              <div className="text-[10px] text-slate-400">{fmtDate(c.created_at)}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Neuer Kommentar…" className="h-8 text-xs" data-testid="kaution-comment-input" />
          <Button size="sm" onClick={addComment} variant="outline" data-testid="kaution-comment-submit">Senden</Button>
        </div>
      </div>

      <div className="pt-2 flex justify-end gap-2">
        {(s.status === "draft" || s.status === "rejected") && (
          <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={del} data-testid="kaution-delete">Löschen</Button>
        )}
      </div>

      {msgOpen && msgCtx && (
        <MessagingDialog
          open={msgOpen}
          onOpenChange={async (o) => {
            setMsgOpen(o);
            if (!o) {
              // Nach Schließen des Composers: Server-Timestamp aktualisieren wenn versendet
              try {
                const { data } = await api.get(`/kaution/settlements/${s.id}`);
                onChange(data);
              } catch {}
            }
          }}
          context={msgCtx}
          defaultSubject={msgCtx.suggested_subject}
          initialAttachmentDocIds={msgCtx.preselected_attachment_ids}
        />
      )}
    </div>
  );
}

function StatCell({ label, value, highlight }) {
  return (
    <div className={`border rounded-md p-2 ${highlight ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={`font-semibold tabular-nums ${highlight ? "text-emerald-800" : ""}`}>{value}</div>
    </div>
  );
}


/* ---------------------------------------------------------------- New Settlement */
function NewSettlementDialog({ onDone, initialTenancyId }) {
  const [tenancies, setTenancies] = useState([]);
  const [tenancyId, setTenancyId] = useState(initialTenancyId || "");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [deductions, setDeductions] = useState([]);
  const [saving, setSaving] = useState(false);
  // 2026-02 — Auszahlungs-Empfänger
  const [payoutRecipient, setPayoutRecipient] = useState("tenant");
  const [paymentIban, setPaymentIban] = useState("");
  const [paymentHolder, setPaymentHolder] = useState("");
  const [tpName, setTpName] = useState("");
  const [tpReason, setTpReason] = useState("");

  useEffect(() => {
    // 2026-03 — Suche via /tenancies/search-close (beendet + mit Kaution). Fallback: alle laden.
    api.get("/tenancies/search-close", { params: { limit: 500 } })
      .then(({ data }) => setTenancies(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => {
        api.get("/tenancies", { params: { limit: 500 } })
          .then(({ data }) => {
            const eligible = (Array.isArray(data) ? data : []).filter((t) =>
              t.termination_status === "cancelled" || t.termination_status === "ended" ||
              t.status === "ended"
            );
            setTenancies(eligible);
          }).catch(() => setTenancies([]));
      });
  }, []);
  // Preselection: sobald tenancies geladen sind und initialTenancyId gesetzt → auswählen
  useEffect(() => {
    if (initialTenancyId && !tenancyId) setTenancyId(initialTenancyId);
    /* eslint-disable-next-line */
  }, [initialTenancyId, tenancies.length]);
  // 2026-02 — Filter/Suche jetzt vollständig im SearchableCombobox
  // (Umlaut-tolerante Suche, Live-Filter). Alle beendeten MVs werden übergeben.
  const _tenLabel = (t) => {
    const name = t.tenant_display_name
      || [t.tenant_first_name, t.tenant_last_name].filter(Boolean).join(" ")
      || t.tenant_company_name
      || t.contract_number
      || t.id.slice(0, 8);
    return name;
  };

  const tenancyOptions = tenancies.map((t) => ({
    id: t.id,
    label: _tenLabel(t),
    sublabel: [t.contract_number, t.property_label || t.property_address_line]
      .filter(Boolean).join(" · "),
    keywords: [
      t.contract_number, t.tenant_display_name, t.tenant_last_name,
      t.tenant_first_name, t.tenant_company_name, t.property_label,
      t.property_address_line,
    ].filter(Boolean).join(" "),
  }));

  const selectedTenancy = tenancies.find((x) => x.id === tenancyId);

  const addDed = () => setDeductions((d) => [...d, { category: "other", description: "", amount: 0 }]);
  const updDed = (i, patch) => setDeductions((d) => d.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const rmDed = (i) => setDeductions((d) => d.filter((_, idx) => idx !== i));

  const deductionTotal = deductions.reduce((sum, d) => sum + Number(d.amount || 0), 0);

  const save = async () => {
    if (!tenancyId) { toast.error("Bitte Mietverhältnis wählen"); return; }
    setSaving(true);
    try {
      await api.post("/kaution/settlements", {
        tenancy_id: tenancyId,
        end_of_tenancy_date: endDate,
        deductions: deductions.map((d) => ({
          category: d.category, description: d.description, amount: Number(d.amount || 0),
        })),
        payment_iban: paymentIban || null,
        payment_holder: paymentHolder || null,
        payout_recipient: payoutRecipient,
        payout_third_party_name: payoutRecipient === "third_party" ? (tpName || null) : null,
        payout_third_party_reason: payoutRecipient === "third_party" ? (tpReason || null) : null,
      });
      toast.success("Abrechnung erstellt");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="kaution-new-dialog">
      <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
        <DialogTitle className="text-lg font-semibold">Neue Kautionsabrechnung</DialogTitle>
        <DialogDescription className="text-xs text-slate-500">
          Endabrechnung nach § 551 BGB — nur bei beendetem Mietverhältnis mit hinterlegter Kaution.
        </DialogDescription>
      </DialogHeader>

      <div className="px-6 py-5 space-y-6">
        {/* 1) Mietverhältnis */}
        <section className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <User size={12} /> 1. Mietverhältnis
          </div>
          <SearchableCombobox
            value={tenancyId}
            onChange={setTenancyId}
            options={tenancyOptions}
            placeholder="Mieter, Firma, Vertragsnr. oder Objekt suchen…"
            emptyText="Keine passenden Mietverhältnisse gefunden"
            testId="kaution-new-tenancy"
          />
          {tenancies.length === 0 && (
            <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-1" data-testid="kaution-new-no-eligible">
              <Info size={14} className="shrink-0 mt-0.5" />
              <span>Keine beendeten Mietverhältnisse vorhanden. Die Endabrechnung ist erst nach Kündigung/Beendigung möglich (§ 551 BGB).</span>
            </div>
          )}
          {selectedTenancy && (
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-700 flex items-center gap-2">
              <Buildings size={13} className="text-slate-400" />
              <span className="font-medium">{selectedTenancy.property_label || "—"}</span>
              {selectedTenancy.property_address_line && (
                <span className="text-slate-500">· {selectedTenancy.property_address_line}</span>
              )}
            </div>
          )}
        </section>

        {/* 2) Ende des Mietverhältnisses */}
        <section className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <CalendarBlank size={12} /> 2. Ende des Mietverhältnisses
          </div>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="h-9 max-w-xs" data-testid="kaution-new-enddate" />
        </section>

        {/* 3) Abzüge */}
        <section className="space-y-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              3. Abzüge <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
            </div>
            <Button size="sm" variant="outline" onClick={addDed} className="h-7 gap-1 text-xs">
              <Plus size={12} /> Abzug hinzufügen
            </Button>
          </div>
          {deductions.length === 0 && (
            <div className="text-xs text-slate-400 italic px-1">Noch keine Abzüge — Kaution wird vollständig ausgezahlt.</div>
          )}
          {deductions.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-2 py-1.5 font-semibold w-40">Kategorie</th>
                    <th className="px-2 py-1.5 font-semibold">Beschreibung</th>
                    <th className="px-2 py-1.5 font-semibold text-right w-28">Betrag</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`kaution-new-deduction-${i}`}>
                      <td className="px-2 py-1">
                        <select value={d.category} onChange={(e) => updDed(i, { category: e.target.value })}
                                className="w-full h-8 border border-slate-200 rounded text-xs px-1.5 bg-white">
                          <option value="outstanding_rent">Restmiete</option>
                          <option value="damage">Schäden</option>
                          <option value="utilities_shortfall">NK-Nachzahlung</option>
                          <option value="cleaning">Reinigung</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <Input value={d.description} onChange={(e) => updDed(i, { description: e.target.value })}
                               placeholder="Beschreibung" className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-1">
                        <Input type="number" step="0.01" value={d.amount}
                               onChange={(e) => updDed(i, { amount: e.target.value })}
                               className="h-8 w-full text-right text-xs tabular-nums" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => rmDed(i)}
                                className="text-slate-400 hover:text-rose-600 p-1"
                                title="Abzug entfernen">
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {deductions.length > 1 && (
                    <tr className="border-t border-slate-200 bg-slate-50">
                      <td colSpan={2} className="px-2 py-1.5 text-xs font-semibold text-slate-700 text-right">Summe Abzüge</td>
                      <td className="px-2 py-1.5 text-xs font-semibold tabular-nums text-right text-rose-700">
                        − {fmtEUR(deductionTotal)}
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* 4) Auszahlungs-Empfänger */}
        <section className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <PaperPlaneTilt size={12} /> 4. Empfänger der Auszahlung
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <PayoutCard
              active={payoutRecipient === "tenant"}
              onClick={() => setPayoutRecipient("tenant")}
              title="An Mieter"
              subtitle="Standard nach § 551 BGB"
              testId="payout-recipient-tenant"
            />
            <PayoutCard
              active={payoutRecipient === "third_party"}
              onClick={() => setPayoutRecipient("third_party")}
              title="An Drittzahler"
              subtitle="z. B. Jobcenter"
              testId="payout-recipient-third-party"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
            <div>
              <Label className="text-xs text-slate-500">IBAN</Label>
              <Input value={paymentIban} onChange={(e) => setPaymentIban(e.target.value)}
                     placeholder="DE00 0000 0000 0000 0000 00" className="h-9 mt-1 font-mono text-sm"
                     data-testid="payment-iban" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Kontoinhaber</Label>
              <Input value={paymentHolder} onChange={(e) => setPaymentHolder(e.target.value)}
                     placeholder={payoutRecipient === "third_party" ? "z. B. Jobcenter Bonn" : "Name des Mieters"}
                     className="h-9 mt-1" data-testid="payment-holder" />
            </div>
          </div>
          {payoutRecipient === "third_party" && (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Drittzahler-Bezeichnung</Label>
                  <Input value={tpName} onChange={(e) => setTpName(e.target.value)}
                         placeholder="z. B. Jobcenter Bonn" className="h-9 mt-1"
                         data-testid="tp-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Grund / Aktenzeichen</Label>
                  <Input value={tpReason} onChange={(e) => setTpReason(e.target.value)}
                         placeholder="z. B. Abtretung, AZ 123" className="h-9 mt-1"
                         data-testid="tp-reason" />
                </div>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>Ohne Abtretungserklärung des Mieters ist eine Auszahlung an den Drittzahler zulässig. Liegt eine Abtretung an den Mieter vor, darf nur der Mieter empfangen.</span>
              </div>
            </div>
          )}
        </section>
      </div>

      <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50/50">
        <Button disabled={saving || !tenancyId} onClick={save}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="kaution-new-save">
          {saving ? "Speichere…" : "Abrechnung anlegen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// Auswahl-Karte für Auszahlungs-Empfänger (Mieter / Drittzahler)
function PayoutCard({ active, onClick, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-3 rounded-md border transition-all ${
        active
          ? "border-teal-600 bg-teal-50 ring-1 ring-teal-600/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
          active ? "border-teal-600" : "border-slate-300"
        }`}>
          {active && <span className="w-2 h-2 rounded-full bg-teal-600" />}
        </span>
        <div className="min-w-0">
          <div className={`text-sm font-medium ${active ? "text-teal-900" : "text-slate-800"}`}>
            {title}
          </div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}


/* ---------------------------------------------------------------- Rates */
function RatesTab() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/kaution/interest-rates");
    setRates(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    try {
      await api.post("/kaution/interest-rates", { year: Number(r.year), rate_pct: Number(r.rate_pct), source: r.source || null, notes: r.notes || null });
      toast.success("Gespeichert");
      setEditRow(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!await window._domivusConfirm("Zinssatz löschen?")) return;
    await api.delete(`/kaution/interest-rates/${id}`);
    load();
  };

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          Domivus-Zinstabelle · § 551 Abs. 3 BGB · Werte pro Jahr, frei anpassbar
        </div>
        <Button size="sm" onClick={() => setEditRow({ year: new Date().getFullYear(), rate_pct: 1.0, source: "", notes: "" })} className="gap-1" data-testid="rate-new">
          <Plus size={14} /> Zinssatz
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm">
        <table className="min-w-full text-sm" data-testid="rates-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Jahr</th>
              <th className="px-3 py-2 text-right">Zinssatz %</th>
              <th className="px-3 py-2">Quelle</th>
              <th className="px-3 py-2">Notizen</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-4 text-slate-400 text-center">Lädt…</td></tr>}
            {!loading && rates.length === 0 && <tr><td colSpan={5} className="p-4 text-slate-400 text-center">Keine Einträge</td></tr>}
            {rates.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`rate-row-${r.year}`}>
                <td className="px-3 py-1.5 tabular-nums">{r.year}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{Number(r.rate_pct).toFixed(2)} %</td>
                <td className="px-3 py-1.5 text-slate-500">{r.source || "—"}</td>
                <td className="px-3 py-1.5 text-slate-500">{r.notes || "—"}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditRow(r)} className="text-xs text-slate-600 hover:text-slate-900 mr-2">Bearbeiten</button>
                  <button onClick={() => del(r.id)} className="text-xs text-rose-600 hover:text-rose-800">Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editRow && (
        <Dialog open onOpenChange={(o) => !o && setEditRow(null)}>
          <DialogContent data-testid="rate-edit-dialog">
            <DialogHeader><DialogTitle>Zinssatz {editRow.id ? "bearbeiten" : "anlegen"}</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <div>
                <Label>Jahr</Label>
                <Input type="number" value={editRow.year} onChange={(e) => setEditRow({ ...editRow, year: e.target.value })} data-testid="rate-year" />
              </div>
              <div>
                <Label>Zinssatz %</Label>
                <Input type="number" step="0.01" value={editRow.rate_pct} onChange={(e) => setEditRow({ ...editRow, rate_pct: e.target.value })} data-testid="rate-pct" />
              </div>
              <div>
                <Label>Quelle</Label>
                <Input value={editRow.source || ""} onChange={(e) => setEditRow({ ...editRow, source: e.target.value })} placeholder="z. B. Sparbuch Ø …" />
              </div>
              <div>
                <Label>Notizen</Label>
                <Textarea rows={2} value={editRow.notes || ""} onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => save(editRow)} className="bg-slate-900 hover:bg-slate-800" data-testid="rate-save">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
